import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Textarea } from '@artosera/ui';
import type { ReglagesEspaceReponse } from '@artosera/shared';
import {
  espaceCollectionneurApi,
  useReglagesEspace,
  useActionEspace,
  type ReglagesAvecHote,
} from '../../api/espaceCollectionneur.js';
import { estApiError } from '../../api/errors.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { NotFoundPage } from '../introuvable/NotFoundPage.js';
import { Field } from '../Field.js';
import styles from './EspaceCollectionneur.module.css';

export function ReglagesEspacePage() {
  const { galerie } = useShellContext();
  if (
    !galerieAModule(galerie, 'espace-collectionneur') ||
    !galerieALaction(galerie, 'collector-space:read')
  )
    return <NotFoundPage />;
  return <Reglages />;
}
function Reglages() {
  const { galerieSlug } = useShellContext();
  const { t } = useTranslation(['regie', 'commun']);
  const reglages = useReglagesEspace(galerieSlug);
  if (reglages.isLoading) return <p role="status">{t('commun:chargement')}</p>;
  if (!reglages.data) return <p role="alert">{t('commun:erreurReseau')}</p>;
  return (
    <Formulaire
      key={reglages.data.version}
      initial={reglages.data}
      recharger={() => {
        void reglages.refetch();
      }}
    />
  );
}
function Formulaire({ initial, recharger }: { initial: ReglagesAvecHote; recharger: () => void }) {
  const { galerie, galerieSlug } = useShellContext();
  const { t } = useTranslation(['regie', 'commun']);
  const id = useId();
  const [accueil, setAccueil] = useState(initial.accueil);
  const [formulaireContact, setFormulaireContact] = useState(initial.formulaireContact);
  const peutEcrire =
    galerieALaction(galerie, 'collector-space:write') && galerieALaction(galerie, 'settings:write');
  const mutation = useActionEspace(galerieSlug, (corps: ReglagesEspaceReponse, appel) =>
    espaceCollectionneurApi.regler(galerieSlug, corps, appel),
  );
  return (
    <section className={styles.page}>
      <h1>{t('espaceCollectionneur.reglages')}</h1>
      <p>
        {t('espaceCollectionneur.adresse')} :{' '}
        <a href={`https://${initial.hote}/espace`}>{`https://${initial.hote}/espace`}</a>
      </p>
      <p>{t('espaceCollectionneur.version', { version: initial.version })}</p>
      <form
        className={styles.formulaire}
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate({ accueil, formulaireContact, version: initial.version });
        }}
      >
        {(['fr', 'en'] as const).map((langue) => (
          <Field
            key={langue}
            htmlFor={`${id}-${langue}`}
            label={t(`espaceCollectionneur.accueil.${langue}`)}
          >
            <Textarea
              id={`${id}-${langue}`}
              value={accueil[langue] ?? ''}
              maxLength={1000}
              readOnly={!peutEcrire}
              onChange={(e) => setAccueil({ ...accueil, [langue]: e.target.value })}
            />
          </Field>
        ))}
        <label>
          <input
            type="checkbox"
            checked={formulaireContact}
            disabled={!peutEcrire}
            onChange={(e) => setFormulaireContact(e.target.checked)}
          />{' '}
          {t('espaceCollectionneur.formulaireContact')}
        </label>
        {peutEcrire && (
          <Button type="submit" loading={mutation.isPending}>
            {t('commun:actions.enregistrer')}
          </Button>
        )}
        {mutation.isSuccess && <p role="status">{t('espaceCollectionneur.enregistre')}</p>}
        {mutation.isError && (
          <>
            <p role="alert">
              {estApiError(mutation.error) && mutation.error.status === 409
                ? t('espaceCollectionneur.conflit')
                : t('commun:erreurReseau')}
            </p>
            <Button
              variant="secondary"
              onClick={() => {
                mutation.reset();
                recharger();
              }}
            >
              {t('espaceCollectionneur.recharger')}
            </Button>
          </>
        )}
      </form>
    </section>
  );
}
